import React, { useMemo, type Ref } from 'react';
import { graphql, useFragment } from 'react-relay';
import RoadmapIcon from '@atlaskit/icon/core/roadmap';
import { CUSTOM_ITEM } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import { PLAN } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/item-type.tsx';
import {
	SECTION_ITEM_RECENT,
	SECTION_ITEM_STARRED,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_ANALYTIC_EVENT_IDS } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/side-nav.tsx';
import {
	ENTITY_ID,
	MENU_ID_PLANS,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import { Nav4MenuLinkItem } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/nav4-menu-link-item/index.tsx';
import { softwarePlanTimelineRoute } from '@atlassian/jira-router-routes-advanced-roadmaps-routes/src/softwarePlanTimelineRoute.tsx';
import { plansDirectoryRoute } from '@atlassian/jira-router-routes-directories-routes/src/plansDirectoryRoute.tsx';
import { generatePath } from '@atlassian/react-resource-router';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useHoverEntryPointTrigger } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/controllers/use-hover-entry-point-trigger/index.tsx';
import {
	useNestedItemDragAndDrop,
	useNavigationItemActions,
} from '@atlassian/navigation-drag-and-drop';
import { mergeRefs } from '@atlassian/jira-merge-refs';
import { PACKAGE_NAME, TEAM_NAME } from '../../../../constants.tsx';
import type { PlanMenuItemRefactored$key } from './__generated__/PlanMenuItemRefactored.graphql';
import { asyncPlanActionMenu } from './async-plan-action-menu/entrypoint.tsx';
import { usePlanReorderingIds } from './use-plan-reordering-ids.tsx';

type Props = {
	fragmentRef: PlanMenuItemRefactored$key;
	favouriteConnectionId: string;
	currentItems?: Array<{ id: string; label: string }>;
	currentOrder?: Array<{ menuId: string; visible: boolean }>;
	item?: {
		id: string;
		label: string;
	};
};

const VIEW_ALL_PLANS_HREF = plansDirectoryRoute.path;

export const PlanMenuItemRefactored = ({
	fragmentRef,
	favouriteConnectionId,
	currentItems,
	currentOrder,
	item,
}: Props) => {
	const data = useFragment<PlanMenuItemRefactored$key>(
		graphql`
			fragment PlanMenuItemRefactored on JiraPlan {
				id
				planId @required(action: THROW)
				title
				isReadOnly
				planStatus
				favouriteValue {
					isFavourite
				}
				scenario @required(action: THROW) {
					scenarioId @required(action: THROW)
				}
				defaultNavigationItem {
					... on JiraSoftwareBuiltInNavigationItem {
						url
					}
				}
			}
		`,
		fragmentRef,
	);

	const planId = data?.planId;
	const isStarred = data?.favouriteValue?.isFavourite ?? false;
	const scenarioId = data?.scenario.scenarioId;
	const planTitle = data?.title || '';
	const planUrl =
		data?.defaultNavigationItem?.url ||
		generatePath(softwarePlanTimelineRoute.path, { planId, scenarioId });
	const isReadOnly = data?.isReadOnly || false;
	const isArchived = data?.planStatus === 'ARCHIVED';

	if (!data || !planId || !scenarioId) {
		return null;
	}

	return (
		<PlanMenuItemRefactoredInner
			id={data.id}
			planId={planId}
			isStarred={isStarred}
			scenarioId={scenarioId}
			planTitle={planTitle}
			isReadOnly={isReadOnly}
			isArchived={isArchived}
			planUrl={planUrl}
			favouriteConnectionId={favouriteConnectionId}
			currentItems={currentItems}
			currentOrder={currentOrder}
			item={item}
		/>
	);
};

type PlanMenuItemRefactoredInnerProps = {
	id: string;
	planId: number;
	isStarred: boolean;
	scenarioId: number;
	planTitle: string;
	isReadOnly: boolean;
	isArchived: boolean;
	planUrl: string;
	favouriteConnectionId: string;
	currentItems?: Array<{ id: string; label: string }>;
	currentOrder?: Array<{ menuId: string; visible: boolean }>;
	item?: {
		id: string;
		label: string;
	};
};

type Analytics = {
	actionSubjectId: string;
	level: number;
	componentType: string;
	itemId: string;
	planId: number;
	section: string;
};

type EntryPointParams = {
	planId: number;
	scenarioId: number;
	planTitle: string;
	isReadOnly: boolean;
	isArchived: boolean;
	isStarred: boolean;
	isUsedForSidebarNav: boolean;
	favouriteConnectionId: string;
};

const PlanMenuItemRefactoredInner = (props: PlanMenuItemRefactoredInnerProps) => {
	const {
		id,
		planId,
		isStarred,
		scenarioId,
		planTitle,
		isReadOnly,
		isArchived,
		planUrl,
		favouriteConnectionId,
		currentItems,
		currentOrder,
		item,
	} = props;
	const analytics: Analytics = useMemo(
		() => ({
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_PLANS],
			level: 1,
			componentType: CUSTOM_ITEM,
			itemId: PLAN,
			planId,
			// TODO BLU-5544 send section via a single <ContextualAnalyticsData instead (which wraps the Starred or Recent panel). Use static object or useMemo, not to have rerendering issues.
			section: isStarred ? SECTION_ITEM_STARRED : SECTION_ITEM_RECENT,
		}),
		[planId, isStarred],
	);

	const entryPointParams: EntryPointParams = useMemo(
		() => ({
			planId,
			scenarioId,
			planTitle,
			isReadOnly,
			isArchived,
			isStarred,
			isUsedForSidebarNav: true,
			favouriteConnectionId,
		}),
		[planId, scenarioId, planTitle, isReadOnly, isArchived, isStarred, favouriteConnectionId],
	);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		asyncPlanActionMenu,
		entryPointParams,
	);

	const triggerRef = useHoverEntryPointTrigger(entryPointActions);

	if (isStarred && currentItems && currentOrder && item) {
		return (
			<DraggablePlanMenuItem
				currentItems={currentItems}
				currentOrder={currentOrder}
				id={id}
				item={item}
				analytics={analytics}
				isStarred={isStarred}
				scenarioId={scenarioId}
				planTitle={planTitle}
				isReadOnly={isReadOnly}
				isArchived={isArchived}
				planUrl={planUrl}
				favouriteConnectionId={favouriteConnectionId}
				entryPointParams={entryPointParams}
			/>
		);
	}

	return (
		scenarioId && (
			<Nav4MenuLinkItem
				analytics={analytics}
				elemBefore={<RoadmapIcon label="" color="currentColor" />}
				href={planUrl || VIEW_ALL_PLANS_HREF}
				menuId={ENTITY_ID.PLAN(String(planId))}
				ref={triggerRef}
				actionsOnHover={
					<JiraEntryPointContainer
						entryPointReferenceSubject={entryPointReferenceSubject}
						id="nav4-sidebar-plan-action-menu"
						packageName={PACKAGE_NAME}
						errorFallback="flag"
						teamName={TEAM_NAME}
						runtimeProps={entryPointParams}
					/>
				}
			>
				{planTitle}
			</Nav4MenuLinkItem>
		)
	);
};

type DraggablePlanMenuItemProps = {
	id: string;
	analytics: Analytics;
	isStarred: boolean;
	scenarioId: number;
	planTitle: string;
	isReadOnly: boolean;
	isArchived: boolean;
	planUrl: string;
	favouriteConnectionId: string;
	currentItems: Array<{ id: string; label: string }>;
	currentOrder: Array<{ menuId: string; visible: boolean }>;
	item: {
		id: string;
		label: string;
	};
	entryPointParams: EntryPointParams;
};

const DraggablePlanMenuItem = ({
	analytics,
	currentItems,
	currentOrder,
	id,
	item,
	planTitle,
	planUrl,
	entryPointParams,
}: DraggablePlanMenuItemProps) => {
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		asyncPlanActionMenu,
		entryPointParams,
	);
	const triggerRef = useHoverEntryPointTrigger(entryPointActions);
	const { entityAri, ownerAri, group } = usePlanReorderingIds();
	const dragAndDropState = useNestedItemDragAndDrop({
		entityAri,
		ownerAri,
		data: {
			id: id.toString(),
			label: planTitle,
		},
		group,
		item: undefined,
		isExpandable: false,
		isExpandOrFlyoutOpen: false,
		id,
		isDragAndDropEnabled: true,
		onExpandFlyoutToggle: () => {
			// No op for dashboard menu item
		},
	});

	const actions = useNavigationItemActions({
		currentItems,
		currentOrder,
		entityAri,
		ownerAri,
		item,
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		getItemId: (i) => (i as { id: string }).id,
	});

	const draggableRef: Ref<HTMLAnchorElement> = dragAndDropState?.draggableRef || null;
	return (
		<>
			<Nav4MenuLinkItem
				analytics={analytics}
				elemBefore={<RoadmapIcon label="" color="currentColor" />}
				href={planUrl || VIEW_ALL_PLANS_HREF}
				menuId={ENTITY_ID.PLAN(String(id))}
				ref={mergeRefs(triggerRef, draggableRef)}
				actionsOnHover={
					<JiraEntryPointContainer
						entryPointReferenceSubject={entryPointReferenceSubject}
						id="nav4-sidebar-plan-action-menu"
						packageName={PACKAGE_NAME}
						errorFallback="flag"
						teamName={TEAM_NAME}
						runtimeProps={{
							...entryPointParams,
							reorderActions: actions,
						}}
					/>
				}
				visualContentRef={dragAndDropState?.dropTargetRef}
				dropIndicator={dragAndDropState?.dropIndicator}
				isDragging={dragAndDropState?.isDragging}
				hasDragIndicator
			>
				{planTitle}
			</Nav4MenuLinkItem>
			{dragAndDropState && dragAndDropState.maybeDragPreview}
		</>
	);
};
